import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Footer from '../components/layout/Footer';
import Header from '../components/layout/Header';
import ApplicationForm from '../components/forms/ApplicationForm';
import SignUp from '../components/forms/SignUp';
import LogIn from '../components/forms/LogIn';
import MyApplication from '../components/forms/MyApplication';
import { menuItems } from '../constants/navigation';
import SaveConfirmModal from '../components/modals/SaveConfirmModal';
import ApplicationTimeModal from '../components/modals/ApplicationTimeModal';
import { EditModeProvider, useEditMode } from '../contexts/EditModeContext';
import { logout as authLogout, getCurrentUserEmail, isAuthenticated, apiClient } from '../services/authService';
import { APPLICATION_START_DATE, APPLICATION_CLOSE_DATE } from '../constants/configLoader';
import { formatDateToEnglish } from '../utils/dateUtils';
import '../styles/pages/apply.css';

const ApplyContent = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [hasApplication, setHasApplication] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [applicationData, setApplicationData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isTestingRefresh, setIsTestingRefresh] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [showApplicationTimeModal, setShowApplicationTimeModal] = useState(false);
  
  // API 호출 중인지 추적하는 ref
  const isFetchingRef = useRef(false);
  // 마지막 API 호출 시간 추적
  const lastFetchTimeRef = useRef(0);
  
  const { 
    isEditMode, 
    setIsEditMode,
    setApplicationState
  } = useEditMode();

  const navigate = useNavigate();
  const location = useLocation();

  // 애플리케이션 시작 날짜와 현재 날짜 비교
  useEffect(() => {
    const checkApplicationPeriod = () => {
      const now = new Date();
      // 테스트 용도로 모달 표시 로직 비활성화 (테스트 완료 후 주석 제거)
      if (now < APPLICATION_START_DATE) {
        // 아직 접수 기간이 시작되지 않았으면 모달 표시
        setShowApplicationTimeModal(true);
      }

      // 테스트 시에는 false로 설정
      // setShowApplicationTimeModal(false);
    };
    
    if (location.pathname === '/apply') {
      checkApplicationPeriod();
    }
  }, [location.pathname]);

  // 로컬 시간으로 변환된 시작/종료 날짜 포맷팅
  const formattedStartDate = formatDateToEnglish(APPLICATION_START_DATE);
  const formattedEndDate = formatDateToEnglish(APPLICATION_CLOSE_DATE);

  // 사용자 신청서 데이터 가져오기
  const fetchUserApplication = useCallback(async (force = false) => {
    if (!isAuthenticated()) return;
    
    // 이미 API 호출 중이면 중복 호출 방지
    if (isFetchingRef.current) {
      console.log('이미 API 호출 중입니다. 중복 호출 방지.');
      return;
    }
    
    // 마지막 API 호출 이후 1초 이내 재호출 방지 (디바운싱)
    const now = Date.now();
    if (!force && now - lastFetchTimeRef.current < 1000) {
      console.log('API 호출 간격이 너무 짧습니다. 스킵합니다.');
      return;
    }
    
    try {
      isFetchingRef.current = true;
      lastFetchTimeRef.current = now;
      setIsLoading(true);
      
      // 첫 번째 API 호출: '/user/apply/preview'
      const response = await apiClient.get(`/user/apply/preview`);
      
      console.log('Preview API 응답:', response.data);
      
      if (response && response.data && response.data.success) {
        // hasApplication이 true면 application 데이터를 사용
        if (response.data.hasApplication) {
          console.log('신청서 있음');
          setApplicationData(response.data.application);
          setHasApplication(true);
          setHasDraft(false); // 신청서가 있으면 초안은 없음
          // EditModeContext에 상태 업데이트
          setApplicationState(true, false);
        } else {
          console.log('신청서 없음, 초안 확인 중...');
          // hasApplication이 false면 draft를 확인하기 위해 두 번째 API 호출
          try {
            const draftResponse = await apiClient.get(`/user/apply/draft/preview`);
            console.log('Draft API 응답:', draftResponse.data);
            // 응답 데이터의 전체 구조와 모든 필드를 자세히 출력
            console.log('Draft 상세 데이터:', JSON.stringify(draftResponse.data, null, 2));
            
            if (draftResponse && draftResponse.data && draftResponse.data.success) {
              if (draftResponse.data.hasDraft) {
                console.log('초안 있음');
                // hasDraft가 true면 draft 데이터를 사용
                setApplicationData(draftResponse.data.draft);
                setHasApplication(false);
                setHasDraft(true);
                // EditModeContext에 상태 업데이트
                setApplicationState(false, true);
              } else {
                console.log('초안 없음, 신규 작성 모드');
                // 둘 다 false면 신규 작성 모드
                setHasApplication(false);
                setHasDraft(false);
                setApplicationData(null);
                // EditModeContext에 상태 업데이트
                setApplicationState(false, false);
              }
            } else {
              console.log('Draft API 호출 실패');
              setHasApplication(false);
              setHasDraft(false);
              setApplicationData(null);
              // EditModeContext에 상태 업데이트
              setApplicationState(false, false);
            }
          } catch (draftError) {
            console.error('초안 데이터 가져오기 실패:', draftError);
            setHasApplication(false);
            setHasDraft(false);
            setApplicationData(null);
            // EditModeContext에 상태 업데이트
            setApplicationState(false, false);
          }
        }
      } else {
        console.log('Preview API 호출 실패');
        setHasApplication(false);
        setHasDraft(false);
        setApplicationData(null);
        // EditModeContext에 상태 업데이트
        setApplicationState(false, false);
      }
    } catch (error) {
      console.error('신청서 데이터 가져오기 실패:', error);
      setHasApplication(false);
      setHasDraft(false);
      setApplicationData(null);
      // EditModeContext에 상태 업데이트
      setApplicationState(false, false);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [setApplicationState]);

  // 초기 로그인 상태 확인
  useEffect(() => {
    if (isAuthenticated()) {
      const email = getCurrentUserEmail();
      setIsLoggedIn(true);
      setUserEmail(email);
      fetchUserApplication(true); // 최초 로드 시에는 강제로 API 호출
    }
  }, [fetchUserApplication]);

  // 페이지 새로고침 이벤트를 한 번만 등록 (중복 방지)
  useEffect(() => {
    // 이 플래그를 사용하여 이미 이벤트가 처리되었는지 확인
    let isReloadHandled = false;
    
    const handlePageReload = () => {
      if (isAuthenticated() && !isReloadHandled) {
        isReloadHandled = true; // 플래그 설정
        console.log('페이지가 새로고침되었습니다. 상태를 다시 확인합니다.');
        
        // setTimeout을 사용하여 중복 이벤트 병합
        setTimeout(() => {
          fetchUserApplication(true);
          // 다음 이벤트를 위해 플래그 재설정
          setTimeout(() => {
            isReloadHandled = false;
          }, 1000);
        }, 100);
      }
    };

    // 이벤트 리스너 등록
    window.addEventListener('load', handlePageReload, { once: true }); // once 옵션으로 한 번만 실행

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener('load', handlePageReload);
    };
  }, [fetchUserApplication]);

  useEffect(() => {
    if (isEditMode) {
      setShowSaveModal(false);
    }
  }, [isEditMode]);

  const executeNavigation = useCallback((target, menuText) => {
    if (menuText === 'APPLY') {
      if (location.pathname === '/apply' && isLoggedIn) {
        return;
      }
      navigate('/apply');
      return;
    }

    if (location.pathname === '/apply') {
      navigate('/', { state: { scrollTarget: target } });
    } else {
      const element = document.querySelector(target);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.scrollBy(0, -80);
      }
    }
  }, [location.pathname, navigate, isLoggedIn]);

  const handleSaveConfirm = useCallback(async () => {
    setShowSaveModal(false);
    setShowConfirmationModal(true);
  }, []);

  const handleSaveCancel = useCallback(() => {
    setShowSaveModal(false);
    setPendingNavigation(null);
  }, []);

  const handleConfirmationClose = useCallback(() => {
    setShowConfirmationModal(false);
    setIsEditMode(false);
    if (pendingNavigation) {
      executeNavigation(pendingNavigation.target, pendingNavigation.menuText);
      setPendingNavigation(null);
    }
    
    // 신청서 데이터 새로 가져오기 - 필요한 경우에만 호출
    // 수정 모드에서 빠져나오는 경우에만 데이터를 갱신합니다
    setTimeout(() => {
      fetchUserApplication(true);
    }, 300);
  }, [executeNavigation, pendingNavigation, setIsEditMode, fetchUserApplication]);

  const handleLogin = useCallback(async (email, password) => {
    try {
      // 상태 업데이트
      setIsLoggedIn(true);
      setUserEmail(email);
      
      // 사용자 신청서 데이터 가져오기 - 여기서는 로그인에 성공한 후 한 번만 호출
      setTimeout(() => {
        fetchUserApplication(true);
      }, 100);
      
      return Promise.resolve();
    } catch (error) {
      console.error('로그인 처리 중 오류:', error);
      return Promise.reject(error);
    }
  }, [fetchUserApplication]);

  const handleLogout = useCallback(() => {
    // authService의 logout 함수 호출 - 콜백 추가
    authLogout(() => {
      // 로컬 상태 업데이트
      setIsLoggedIn(false);
      setUserEmail('');
      setHasApplication(false);
      setHasDraft(false);
      setApplicationData(null);
      setIsEditMode(false);
      // EditModeContext에 상태 업데이트
      setApplicationState(false, false);
      
      console.log("로그아웃 상태 업데이트 완료");
    });
  }, [setIsEditMode, setApplicationState]);

  const handleEdit = useCallback(() => {
    console.log('Edit button clicked, checking conditions...');
    // Edit 버튼을 눌렀을 때만 상태를 변경
    if (hasApplication || hasDraft) {
      console.log('Valid application or draft exists, setting edit mode to true');
      setIsEditMode(true);
    } else {
      console.log('No application or draft exists, cannot enter edit mode');
    }
  }, [setIsEditMode, hasApplication, hasDraft]);

  // 토큰 리프레시 테스트 핸들러
  const handleTestRefreshToken = async () => {
    if (import.meta.env.DEV) {
      setIsTestingRefresh(true);
      try {
        // 리프레시 후 데이터를 다시 불러옴
        await fetchUserApplication(true); // 강제로 API 호출
      } catch (error) {
        console.error('토큰 리프레시 테스트 중 오류:', error);
      } finally {
        setIsTestingRefresh(false);
      }
    }
  };

  const handleCloseApplicationTimeModal = () => {
    setShowApplicationTimeModal(false);
  };

  const renderContent = () => {
    // 모달이 표시 중일 때는 내용을 렌더링하지 않음
    if (showApplicationTimeModal) {
      return null;
    }
    
    if (!isLoggedIn) {
      if (showSignUp) {
        return <SignUp onShowLogin={() => setShowSignUp(false)} />;
      }
      return (
        <LogIn 
          onLogin={handleLogin}
          onShowSignUp={() => setShowSignUp(true)}
        />
      );
    }

    if (isLoading) {
      return <div className="loading">Loading data...</div>;
    }

    if (hasApplication && !isEditMode && applicationData) {
      return <MyApplication 
        application={applicationData}
        onEdit={handleEdit}
        isLoggedIn={isLoggedIn}
        userEmail={userEmail}
        onLogout={handleLogout}
      />;
    }

    // mode 결정 로직: edit 모드 또는 신규 작성
    let formMode = 'create'; // 기본값은 신규 작성
    
    if (isEditMode) {
      // 편집 모드가 활성화된 경우
      formMode = 'edit';
    } else if (hasApplication) {
      // 신청서가 있는 경우(그러나 위의 조건에 따라 여기에 도달하지 않을 수 있음)
      formMode = 'edit';
    } else if (hasDraft) {
      // 초안이 있는 경우
      formMode = 'edit';
    }
    
    console.log('결정된 폼 모드:', formMode, {hasApplication, hasDraft, isEditMode});

    return <ApplicationForm 
      mode={formMode}
      hasApplication={hasApplication}
      hasDraft={hasDraft}
      initialData={applicationData}
      onSave={handleSaveConfirm}
      onCancel={handleSaveCancel}
      isLoggedIn={isLoggedIn}
      userEmail={userEmail}
      onLogout={handleLogout}
    />;
  };

  return (
    <div className="container">
      <>
        <Header 
          menuItems={menuItems} 
          isEditing={isEditMode}
          onSave={handleSaveConfirm}
        />
        {/* {import.meta.env.DEV && isLoggedIn && (
          <div style={{ 
            padding: '10px', 
            textAlign: 'center', 
            background: '#f0f0f0',
            border: '1px solid #ccc',
            margin: '0 auto',
            maxWidth: '1200px'
          }}>
            <button 
              onClick={handleTestRefreshToken}
              disabled={isTestingRefresh}
              style={{
                padding: '5px 10px',
                backgroundColor: '#ff6b6b',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isTestingRefresh ? 'default' : 'pointer',
                opacity: isTestingRefresh ? 0.7 : 1
              }}
            >
              {isTestingRefresh ? '테스트 중...' : '[개발] 토큰 만료 및 리프레시 테스트'}
            </button>
          </div>
        )} */}
        <SaveConfirmModal 
          isOpen={showSaveModal && !showConfirmationModal}
          onConfirm={handleSaveConfirm}
          onCancel={handleSaveCancel}
          hasApplication={hasApplication}
          hasDraft={hasDraft}
        />
        <SaveConfirmModal 
          isOpen={showConfirmationModal}
          onConfirm={handleConfirmationClose}
          onCancel={handleConfirmationClose}
          hasApplication={hasApplication}
          hasDraft={hasDraft}
          isConfirmation={true}
        />
        <ApplicationTimeModal
          isOpen={showApplicationTimeModal}
          onClose={handleCloseApplicationTimeModal}
          startDate={formattedStartDate}
          endDate={formattedEndDate}
        />
      </>
      <main role="main" className="apply_page">
        <div className="auth-container">
          {renderContent()}
        </div>
      </main>
      <Footer />
    </div>
  );
};

const Apply = () => {
  return (
    <EditModeProvider>
      <ApplyContent />
    </EditModeProvider>
  );
};

export default Apply; 